import React, { useMemo, useCallback } from 'react'
import Grid from '@material-ui/core/Grid'
import { useSelector, useDispatch } from 'react-redux'

import { number } from 'Utils/Validators/formValidation'
import { processInventoryMovement } from 'Reducers/Inventory/actions'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { Input } from 'Components/Form/Input'
import Button from 'Components/Form/Button'
import Form from 'Components/Form'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import { getCampaignById as getCampaignByIdGQL } from 'Module/graphQL'
import { isPowerBankTypeName } from 'Utils/PowerBanks'

import { useInventoryRequests } from '../../gql'
import { useTypeAndClientHashmap } from '../../utils'
import { INVENTORY_FLYER_ITEM_TYPE } from '../../../../../../shared/constants'

const InventoryEditModal = props => {
  const { handleCancel, handleConfirm, item, cityId } = props
  const { editInventoryItem } = useInventoryRequests()
  const dispatch = useDispatch()
  const { clients } = useSelector(state => ({
    clients: state.clients.clientsList,
  }))

  const { typesHashmap } = useTypeAndClientHashmap()

  const { type, client } = useMemo(() => {
    const type = typesHashmap[item.typeId].name
    const client = clients.find(client => client.id === item.clientId)
    return { type, client }
  }, [item, typesHashmap])
  // A power bank row is one physical unit: its quantity is never adjusted
  // by hand (that would break the serial/hold model). The table hides the
  // edit button for these rows, this is the backstop.
  const isPowerBank = isPowerBankTypeName(type)

  const mapResponse = data => ({
    id: data.campaign.id,
    artworksLean: data.campaign.artworksLean || [],
  })

  const { data: campaign = {}, isLoading: isCampaignLoading } = useGraphQLFetch(
    {
      fetchFn: ({ campaignId }) => getCampaignByIdGQL(campaignId),
      defaultFetchArgs: { campaignId: item.campaignId },
      mapResponse,
      skipFetchOnInit: !item.campaignId,
      defaultData: {},
    },
  )

  const artworksOptions = useMemo(() => {
    if (!item.campaignId || !campaign || isCampaignLoading) return []
    const artworks = campaign.artworksLean || []
    return artworks.map(a => ({ value: a.id, label: `${a.id} - ${a.name}` }))
  }, [campaign, item.campaignId, isCampaignLoading])

  const onConfirm = useCallback(
    async (e, { data }) => {
      const { quantity, tag, artwork } = data

      if (
        (tag && tag.value !== item.tag) ||
        (artwork && artwork.value !== item.campaignArtworkId)
      ) {
        await editInventoryItem({
          id: Number(item.id),
          tag: tag.value,
          campaignArtworkId: artwork.value,
        })
        handleConfirm()
      }

      if (quantity.value !== item.initialAmount) {
        const adjustment = -(item.initialAmount - quantity.value)
        dispatch(
          processInventoryMovement({
            cityId: Number(cityId),
            quantity: adjustment,
            inventoryId: Number(item.id),
            ambassadorId: null,
            comments:
              adjustment < 0
                ? 'Deleted on inventory page.'
                : 'Added on inventory page.',
            movement: 0,
            dateTime: new Date().toISOString(),
          }),
        )
        setTimeout(handleConfirm, 1000)
      }
    },
    [item, cityId],
  )

  if (isPowerBank) {
    return (
      <Modal show onHide={handleCancel}>
        <ModalHeader closeButton>
          <ModalTitle>Power bank "{item.description}"</ModalTitle>
        </ModalHeader>
        <ModalBody>
          Power banks are one unit per row and cannot be edited here. Add units
          with the CSV upload and hand them over or take them back on the pickup
          page.
        </ModalBody>
        <ModalFooter>
          <Button
            className="submit-button"
            value="OK"
            onClick={handleCancel}
            cypress_id={'cancelBtn'}
          />
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Modal show onHide={handleCancel}>
      <Form>
        <ModalHeader closeButton>
          <ModalTitle>
            Edit {type} "{item.description}" for {client.name}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Grid spacing={3} container>
            <Grid item xs={6}>
              <Input
                name="quantity"
                label="Quantity"
                value={item.initialAmount}
                validator={[number]}
                cypress_id={'quantityInput'}
              />
            </Grid>
            {item.typeId == 3 && (
              <Grid item xs={6}>
                <Input
                  name="tag"
                  label="Tag"
                  value={item.tag}
                  cypress_id={'tagInput'}
                />
              </Grid>
            )}
            {item.typeId == INVENTORY_FLYER_ITEM_TYPE && (
              <Grid item xs={12}>
                <SelectDashboard
                  label="Artwork"
                  name="artwork"
                  options={artworksOptions || []}
                  value={item.campaignArtworkId}
                  sorted
                  cypress_id={'artworkSelect'}
                />
              </Grid>
            )}
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button
            className="submit-button"
            value="Cancel"
            onClick={handleCancel}
            cypress_id={'cancelBtn'}
          />
          <Button value="Go" onClick={onConfirm} cypress_id={'goBtn'} />
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default InventoryEditModal
